<template>
  <el-collapse
    v-model="packageDetailsCollapse"
    class="collapse-box"
  >
    <ApolloQuery
      :query="packageByPackageNumberQuery"
      :variables="{package_number: packageNumber}"
      :fetch-policy="'network-only'"
      @result="handlePackageResult"
    >
      <template #default="{result: {data}, isLoading}">
        <el-collapse-item
          v-if="isLoading"
          v-loading="isLoading"
        >
          <el-container>
            <el-main
              class="collapse-content loader"
            />
          </el-container>
        </el-collapse-item>

        <el-collapse-item
          v-if="!isLoading"
          :title="$t('Details of the package') + ' ' + data.packageByPackageNumber.package_number"
          name="1"
        >
          <el-container
            v-if="data && data.packageByPackageNumber && !isLoading"
          >
            <el-main
              class="collapse-content"
            >
              <el-container class="package-details-element">
                <el-main class="package-details-data">
                  {{ packageType || '–' }}
                </el-main>
                <el-footer class="package-details-title">
                  {{ $t('Type') }}
                </el-footer>
              </el-container>

              <el-container class="package-details-element">
                <el-main class="package-details-data">
                  <span v-if="packageType === 'Badge LiberT'">
                    {{ data.packageByPackageNumber.atlandes_client_code || '–' }}
                  </span>
                  <span v-else>
                    {{ data.packageByPackageNumber.cm_32_4_codclient || '–' }}
                  </span>
                </el-main>
                <el-footer class="package-details-title">
                  {{ packageType === 'Badge LiberT' ? $t('atlandesClientCode') : $t('Code client') }}
                </el-footer>
              </el-container>

              <el-container class="package-details-element">
                <el-main class="package-details-data">
                  <el-tooltip
                    placement="top"
                    effect="light"
                    popper-class="address-tooltip"
                  >
                    <section
                      slot="content"
                    >
                      <el-row>
                        <el-col
                          :span="7"
                          class="tooltip-title"
                        >
                          {{ $t('Company Name') }}:
                        </el-col>
                        <el-col
                          :span="17"
                          class="tooltip-data"
                        >
                          {{ data.packageByPackageNumber.address.company }}
                        </el-col>
                      </el-row>

                      <el-row>
                        <el-col
                          :span="7"
                          class="tooltip-title"
                        >
                          {{ $t('Address') }}:
                        </el-col>
                        <el-col
                          :span="17"
                          class="tooltip-data"
                        >
                          {{ data.packageByPackageNumber.address.street_1 }}
                        </el-col>
                      </el-row>

                      <el-row v-show="data.packageByPackageNumber.address.street_2">
                        <el-col
                          :span="7"
                          class="tooltip-title"
                        >
                          {{ $t('Additional address') }}:
                        </el-col>
                        <el-col
                          :span="17"
                          class="tooltip-data"
                        >
                          {{ data.packageByPackageNumber.address.street_2 }}
                        </el-col>
                      </el-row>

                      <el-row v-show="data.packageByPackageNumber.address.street_3">
                        <el-col
                          :span="7"
                          class="tooltip-title"
                        >
                          {{ $t('Additional address') }}:
                        </el-col>
                        <el-col
                          :span="17"
                          class="tooltip-data"
                        >
                          {{ data.packageByPackageNumber.address.street_3 }}
                        </el-col>
                      </el-row>

                      <el-row>
                        <el-col
                          :span="7"
                          class="tooltip-title"
                        >
                          {{ $t('City') }}:
                        </el-col>
                        <el-col
                          :span="17"
                          class="tooltip-data"
                        >
                          {{ data.packageByPackageNumber.address.city }}
                        </el-col>
                      </el-row>

                      <el-row>
                        <el-col
                          :span="7"
                          class="tooltip-title"
                        >
                          {{ $t('Zipcode') }}:
                        </el-col>
                        <el-col
                          :span="17"
                          class="tooltip-data"
                        >
                          {{ data.packageByPackageNumber.address.zipcode }}
                        </el-col>
                      </el-row>

                      <el-row>
                        <el-col
                          :span="7"
                          class="tooltip-title"
                        >
                          {{ $t('Country') }}:
                        </el-col>

                        <el-col
                          :span="17"
                          class="tooltip-data"
                        >
                          {{ data.packageByPackageNumber.address.country.name }}
                        </el-col>
                      </el-row>
                    </section>
                    <span>{{ data.packageByPackageNumber.address.name || '–' }}</span>
                  </el-tooltip>
                </el-main>

                <el-footer class="package-details-title">
                  {{ $t('Client name') }}
                </el-footer>
              </el-container>

              <el-container class="package-details-element">
                <el-main class="package-details-data">
                  {{ formattedDepartment(data.packageByPackageNumber.department) || '–' }}
                </el-main>
                <el-footer class="package-details-title">
                  {{ $t('Filiale') }}
                </el-footer>
              </el-container>

              <el-container class="package-details-element">
                <el-main class="package-details-data">
                  {{ data.packageByPackageNumber.shipped_at ? formatDate(data.packageByPackageNumber.shipped_at) : '–' }}
                </el-main>
                <el-footer class="package-details-title">
                  {{ $t('Date') }}
                </el-footer>
              </el-container>

              <el-container class="package-details-element">
                <el-main class="package-details-data">
                  <el-tooltip
                    placement="top"
                    effect="light"
                    popper-class="package-item-tooltip"
                  >
                    <section
                      slot="content"
                      class="package-item-tooltip-content"
                    >
                      <el-row>
                        <el-col
                          :span="24"
                          class="package-item-tooltip-title"
                        >
                          {{ data.packageByPackageNumber.flow === 'TAG_B2C' ? 'FIDU' : 'PAN' }}
                        </el-col>
                      </el-row>

                      <el-row class="scrollable-content">
                        <el-col
                          v-if="data.packageByPackageNumber.flow === 'TAG_B2C'"
                          :span="24"
                          class="package-item-tooltip-body"
                        >
                          <el-tag
                            v-for="item in data.packageByPackageNumber.fidu"
                            :key="item"
                            size="mini"
                            style="padding: 0 4px; margin: 0; height: fit-content;"
                          >
                            {{ item }}
                          </el-tag>
                        </el-col>

                        <el-col
                          v-else
                          :span="24"
                          class="package-item-tooltip-body"
                        >
                          <el-tag
                            v-for="item in data.packageByPackageNumber.pan"
                            :key="item"
                            size="mini"
                            class="fidu-pan-tag"
                          >
                            {{ item }}
                          </el-tag>
                        </el-col>
                      </el-row>
                    </section>

                    <span>{{ data.packageByPackageNumber.package_items_count || '0' }}</span>
                  </el-tooltip>
                </el-main>
                <el-footer class="package-details-title">
                  {{ $t('Number of badges') }}
                </el-footer>
              </el-container>
            </el-main>
          </el-container>
        </el-collapse-item>
      </template>
    </ApolloQuery>
  </el-collapse>
</template>

<script lang="ts">
import Vue from 'vue'
import { Component, Prop } from 'vue-property-decorator'
import { Department, PackageByPackageNumberQuery } from '@/types/graphql'
import { formatDate } from '@/lib/date'
import formatDepartment from '@/lib/formatDepartment'
import { ApolloQueryResult } from 'apollo-client'

type PackageType = 'Badge LiberT' | 'Badge PASSANGO' | 'OBU PASSANGO'

@Component({})
export default class extends Vue {
  @Prop({ required: true })
  private packageNumber!: string;

  private packageByPackageNumberQuery = PackageByPackageNumberQuery;

  private packageDetailsCollapse = ['1'];

  private packageType: PackageType | undefined;

  private packageTypeMap: Record<string, PackageType> = {
    'App\\Models\\Packages\\LibertPackage': 'Badge LiberT',
    'App\\Models\\Packages\\HgvPackage': 'Badge PASSANGO',
    'App\\Models\\Packages\\SiemensPackage': 'OBU PASSANGO'
  };

  private setPackageType(input: string | undefined) {
    if (input) {
      this.packageType = this.packageTypeMap[input]
    }
  }

  private formatDate = (date: string) => formatDate(date);

  private formattedDepartment = (department: Department) =>
    formatDepartment(department);

  private handlePackageResult(result: ApolloQueryResult<PackageByPackageNumberQuery>) {
    this.setPackageType(result.data.packageByPackageNumber?.package_type)

    this.$emit('result', result.data.packageByPackageNumber)
  }
}
</script>

<style lang="scss">
@import "@/css/ticketCreateEdit.scss";

.package-item-tooltip {
  width: unquote("max(25%, 300px)");
  height: 200px;

  .package-item-tooltip-content {
    height: 100%;

    .scrollable-content {
      height: calc(100% - 20px);
      padding-top: 10px;
      overflow: auto;
    }
  }

  .package-item-tooltip-title {
    display: flex;
    align-items: center;
    font-weight: bold;
    border-bottom: 1px solid grey;
    height: 20px;
  }

  .package-item-tooltip-body {
    height: fit-content;
    display: flex;
    flex-wrap: wrap;
    gap: 5px;

    .fidu-pan-tag {
      padding: 0 4px;
      margin: 0;
      height: fit-content;
    }
  }
}
</style>
